import { cn } from '@/lib/utils';
import { Link } from '@tanstack/react-router';
import { ChevronRight, LucideIcon } from 'lucide-react';

interface CardProps {
  title?: string;
  titleWrap?: 'auto' | 'always' | 'never';
  info?: string;
  helperText?: string;
  icon?: LucideIcon;
  action?: Record<string, string> & { link: string; label: string };
  className?: string;
  infoClassName?: string;
  helperTextClassName?: string;
}

const titleWrapClasses = {
  auto: 'whitespace-normal',
  always: '',
  never: 'whitespace-nowrap',
};

export const InfoCard = ({
  title,
  titleWrap = 'auto',
  icon,
  info,
  action,
  helperText,
  className,
  infoClassName,
  helperTextClassName,
}: CardProps) => {
  const [firstWord, ...rest] = (title ?? '').split(' ');
  const restText = rest.join(' ');

  return (
    <div className={cn('relative rounded-sm border border-contrast bg-card py-3 pl-4 pr-10 ', className)}>
      <div className="w-7 h-7 bg-surface-inverse  absolute top-0 right-0 flex items-center justify-center rounded-bl-sm rounded-tr-xs">
        {icon && <IconWrapper Icon={icon} />}
      </div>
      <div className="flex flex-col justify-between h-full gap-3">
        <div className="flex justify-between gap-2">
          {titleWrap === 'always' && title ? (
            <h2 className="min-w-0 text-xs font-bold leading-tight line-clamp-2">
              <span className="block">{firstWord}</span>
              {restText ? <span className="block line-clamp-1">{restText}</span> : null}
            </h2>
          ) : (
            <h2 className={cn('min-w-0 text-xs font-bold leading-none', titleWrapClasses[titleWrap])}>{title}</h2>
          )}
          {action && (
            <Link className="flex gap-0.5 items-center h-3 cursor-pointer" to={action?.link} role="button" tabIndex={0}>
              <div className="text-xs font-medium whitespace-nowrap">{action.label}</div>
              <ChevronRight size={10} color="var(--contrast)" />
            </Link>
          )}
        </div>
        <div className="flex gap-1 items-baseline font-display">
          <p className={cn('text-stat font-medium leading-none truncate', infoClassName)}>{info ?? ''}</p>
          <p className={cn('text-xl leading-none font-body', helperTextClassName)}>{helperText}</p>
        </div>
      </div>
    </div>
  );
};

const IconWrapper = ({ Icon }: { Icon: LucideIcon }) => {
  return <Icon size={20} className="text-foreground-inverse" />;
};
